/**
 * Cookie-consent context objects and hooks.
 *
 * `.ts` (no JSX) so `ConsentProvider.tsx` stays a Fast Refresh component boundary.
 */

import { createContext, useContext } from 'react';
import type { ConsentContextValue } from '../types/consent';

/**
 * React context object. Exported for `ConsentProvider` only — consumers use `useConsent`.
 */
export const ConsentContext = createContext<ConsentContextValue | undefined>(undefined);

/**
 * Internal context for modal state (used by CookiePreferencesModal).
 */
export interface ConsentModalContextValue {
  isOpen: boolean;
  doNotSellMode: boolean;
  close: () => void;
}

/**
 * React context object. Exported for `ConsentProviderWithModal` only —
 * consumers use `useConsentModal`.
 */
export const ConsentModalContext = createContext<ConsentModalContextValue | undefined>(undefined);

/**
 * Hook to access consent context.
 * Must be used within a ConsentProvider or ConsentProviderWithModal.
 *
 * @throws Error if used outside provider
 */
export const useConsent = (): ConsentContextValue => {
  const context = useContext(ConsentContext);
  if (context === undefined) {
    throw new Error('useConsent must be used within a ConsentProvider');
  }
  return context;
};

/**
 * Hook to access modal context (for CookiePreferencesModal).
 * Must be used within ConsentProviderWithModal.
 */
export const useConsentModal = (): ConsentModalContextValue => {
  const context = useContext(ConsentModalContext);
  if (context === undefined) {
    throw new Error('useConsentModal must be used within ConsentProviderWithModal');
  }
  return context;
};
